import { useContext, useEffect, useState } from "react";
import Modal from "../utils/modal";
import Loading from "../atoms/loading";
import Button from "../atoms/button";
import ToastContext from "../../context/toastProvider";
import { statusLabel } from "../artLabels/status";
import { getArtLabelByModel, linkArtLabel } from "../artLabels/functions";
import { createExtendedArtDraft, extendedArtErrorMessage, updateExtendedArtSerial } from "./functions";
import ArtSourceTabs, { modelsLabel } from "./ArtSourceTabs";
import { uploadArtSource } from "./uploadArtSource";
import { formatDayMonth } from "./format";

const errorMessage = (out, fallback) => extendedArtErrorMessage(out?.code, out?.message || fallback);

/**
 * Passo "Arte da etiqueta" (spec §5.2): arte já vinculada ao modelo pré-selecionada;
 * sem arte ou em "Trocar", abas Sugestões / Biblioteca / Enviar imagem da carta.
 * Confirmar = POST /link (move o modelo para a arte) + POST /drafts.
 */
const ArtLabelStep = ({ user, order, model, serial = null, onDone, onCancel }) => {
    const { goodToast, badToast } = useContext(ToastContext);
    const [loading, setLoading] = useState(true);
    const [current, setCurrent] = useState(null);
    const [mode, setMode] = useState("current");
    const [choice, setChoice] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            const out = await getArtLabelByModel(user?.token, model._id);
            if (!alive) return;
            if (out?.type !== "success") {
                badToast(errorMessage(out, "Erro ao buscar a arte do modelo"));
            }
            const art = out?.type === "success" ? out.data : null;
            setCurrent(art || null);
            setMode(art ? "current" : "choose");
            setLoading(false);
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.token, model._id]);

    const createDraft = async (artKey, status) => {
        const out = await createExtendedArtDraft(user?.token, { orderId: order._id, gradableModelId: model._id, artKey });
        if (out?.type !== "success") {
            badToast(errorMessage(out, "Erro ao criar o item"));
            return;
        }
        if (status === "approved") {
            goodToast("Arte já aprovada");
        } else {
            const prazo = formatDayMonth(order.calculatedDeadline);
            goodToast(prazo ? `Arte na fila (prazo ${prazo})` : "Arte na fila");
        }
        onDone(out.data);
    };

    // Com `serial` (ação "Trocar arte" do item): troca a arte do item em vez de criar rascunho.
    const commit = async (artKey, status) => {
        if (!serial) {
            await createDraft(artKey, status);
            return;
        }
        const out = await updateExtendedArtSerial(user?.token, serial._id, { artKey });
        if (out?.type !== "success") {
            badToast(errorMessage(out, "Erro ao trocar a arte do item"));
            return;
        }
        goodToast(status === "approved" ? "Arte do item trocada (já aprovada)" : "Arte do item trocada (na fila)");
        onDone(out.data);
    };

    const confirmCurrent = async () => {
        setSaving(true);
        try {
            await commit(current.artKey, current.status);
        } finally {
            setSaving(false);
        }
    };

    const linkBody = async () => {
        if (choice.type === "tcgdex") {
            return { gradableModelId: model._id, provider: "tcgdex", cardId: choice.cardId, lang: choice.lang };
        }
        if (choice.type === "library") {
            return { gradableModelId: model._id, artKey: choice.artKey };
        }
        const uploaded = await uploadArtSource(user?.token, choice.file);
        return { gradableModelId: model._id, provider: "upload", artKey: uploaded.artKey, s3Key: uploaded.s3Key, cardName: choice.cardName };
    };

    const confirmChoice = async () => {
        setSaving(true);
        try {
            const body = await linkBody();
            const out = await linkArtLabel(user?.token, body);
            if (out?.type !== "success") {
                badToast(errorMessage(out, "Erro ao vincular a arte"));
                return;
            }
            await commit(out.data.artKey, out.data.status);
        } catch (error) {
            badToast(error?.message || "Erro no envio da imagem");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal width="min(96%, 960px)">
            <div className="p-2 text-left text-laranjinha">
                <h2 className="mb-3 text-lg font-semibold text-primary">{serial ? "Trocar arte" : "Arte da etiqueta"} — {model.name}</h2>
                {loading ? <Loading classes="m-auto" /> : null}

                {!loading && mode === "current" && current ? (
                    <div className="flex flex-wrap items-center gap-4">
                        {current.thumbUrl ? <img alt={`Arte ${current.cardName}`} src={current.thumbUrl} className="h-32 rounded" /> : null}
                        <div>
                            <p className="font-semibold">{current.cardName}</p>
                            <p className="text-xs text-laranjinha/60">{current.artKey} · {statusLabel(current.status)} · atende {modelsLabel((current.gradableModels || []).length)}</p>
                            <div className="mt-2 flex gap-2">
                                <Button classes="bg-green-400 hover:bg-green-300 px-4" click={confirmCurrent} disabled={saving}>Usar esta</Button>
                                <Button classes="bg-mgs-elevated text-laranjinha border border-primary/40 px-4" click={() => setMode("choose")} disabled={saving}>Trocar</Button>
                            </div>
                        </div>
                    </div>
                ) : null}

                {!loading && mode === "choose" ? (
                    <div className="flex flex-col gap-3">
                        {current ? (
                            <p className="rounded border border-amber-500/60 bg-amber-950/40 px-3 py-2 text-sm">
                                {`Esta arte atende ${modelsLabel((current.gradableModels || []).length)}. Trocar a arte deste modelo move só ele; seriais já emitidos não mudam.`}
                            </p>
                        ) : null}
                        <ArtSourceTabs user={user} model={model} choice={choice} onChoose={setChoice} />
                        {choice ? <p className="text-sm">Escolhida: {choice.label}</p> : null}
                        <div className="flex flex-wrap gap-2">
                            <Button classes="bg-green-400 hover:bg-green-300 px-4" click={confirmChoice} disabled={!choice || saving}>Confirmar arte</Button>
                            {current ? (
                                <Button classes="bg-mgs-elevated text-laranjinha border border-primary/40 px-4" click={() => setMode("current")} disabled={saving}>Voltar à arte atual</Button>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                <div className="mt-4">
                    <Button classes="bg-red-300 hover:bg-red-400 px-4" click={onCancel} disabled={saving}>Cancelar</Button>
                </div>
            </div>
        </Modal>
    );
};

export default ArtLabelStep;
